import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Bookmark, BookmarkPlus, Pin, PinOff, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export type SavedViewEntity = "leads" | "clients" | "invoices" | "schedule";

export interface SavedView {
  id: string;
  workspaceId: string;
  userId: string;
  entity: SavedViewEntity;
  name: string;
  params: Record<string, unknown>;
  pinned: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface SavedViewsTabStripProps {
  entity: SavedViewEntity;
  getCurrentParams: () => Record<string, unknown>;
  applyParams: (params: Record<string, unknown>) => void;
  /** Used to render an "active" indicator if the current state matches a view. */
  activeViewId?: string | null;
  onActiveViewChange?: (id: string | null) => void;
  /** Optional content rendered to the right of the saved-view tabs (e.g. search bar + geotags). */
  rightSlot?: React.ReactNode;
}

export function SavedViewsTabStrip({
  entity,
  getCurrentParams,
  applyParams,
  activeViewId,
  onActiveViewChange,
  rightSlot,
}: SavedViewsTabStripProps) {
  const { toast } = useToast();
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");

  const { data: views = [] } = useQuery<SavedView[]>({
    queryKey: ["/api/saved-views", { entity }],
  });

  const pinned = views.filter((v) => v.pinned);
  const unpinned = views.filter((v) => !v.pinned);

  const createMutation = useMutation({
    mutationFn: async (input: { name: string; params: Record<string, unknown>; pinned: boolean }) => {
      const res = await apiRequest("POST", "/api/saved-views", { entity, ...input });
      return res.json();
    },
    onSuccess: (row: SavedView) => {
      queryClient.invalidateQueries({ queryKey: ["/api/saved-views", { entity }] });
      setSaveDialogOpen(false);
      setNewName("");
      onActiveViewChange?.(row.id);
      toast({ title: "View saved", description: `"${row.name}" is now pinned to your tab strip.` });
    },
    onError: (err: any) => {
      toast({
        title: "Could not save view",
        description: err?.message || "Try a different name.",
        variant: "destructive",
      });
    },
  });

  const togglePinMutation = useMutation({
    mutationFn: async (v: SavedView) => {
      const res = await apiRequest("PATCH", `/api/saved-views/${v.id}`, { pinned: !v.pinned });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/saved-views", { entity }] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (v: SavedView) => {
      await apiRequest("DELETE", `/api/saved-views/${v.id}`);
    },
    onSuccess: (_d, v) => {
      queryClient.invalidateQueries({ queryKey: ["/api/saved-views", { entity }] });
      if (activeViewId === v.id) onActiveViewChange?.(null);
    },
  });

  const handleApply = (v: SavedView) => {
    applyParams(v.params);
    onActiveViewChange?.(v.id);
  };

  const handleOpenSave = () => {
    setNewName("");
    setSaveDialogOpen(true);
  };

  const handleConfirmSave = () => {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate({ name, params: getCurrentParams(), pinned: true });
  };

  return (
    <>
      <div
        className="flex items-center gap-2 overflow-x-auto px-2 py-1 border-b bg-background"
        data-testid={`saved-views-strip-${entity}`}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs shrink-0"
          onClick={handleOpenSave}
          data-testid={`button-save-view-${entity}`}
        >
          <BookmarkPlus className="h-3 w-3 mr-1" />
          Save view
        </Button>
        {pinned.length === 0 && (
          <span className="text-xs text-muted-foreground px-2 shrink-0">No saved views yet</span>
        )}
        {pinned.map((v) => {
          const active = activeViewId === v.id;
          return (
            <div key={v.id} className="flex items-center group">
              <Button
                variant={active ? "default" : "ghost"}
                size="sm"
                className="h-7 px-2 rounded-r-none text-xs"
                onClick={() => handleApply(v)}
                data-testid={`saved-view-tab-${v.id}`}
                title={v.name}
              >
                {active && <Check className="h-3 w-3 mr-1" />}
                <span className="max-w-[160px] truncate">{v.name}</span>
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={active ? "default" : "ghost"}
                    size="sm"
                    className="h-7 px-1 rounded-l-none border-l border-background/30"
                    aria-label={`Manage view ${v.name}`}
                    data-testid={`saved-view-menu-${v.id}`}
                  >
                    <Bookmark className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => togglePinMutation.mutate(v)}>
                    <PinOff className="h-4 w-4 mr-2" /> Unpin
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => deleteMutation.mutate(v)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete view
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        })}
        {unpinned.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                data-testid={`saved-views-more-${entity}`}
              >
                More ({unpinned.length})
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-80 overflow-y-auto">
              {unpinned.map((v) => (
                <DropdownMenuItem key={v.id} className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    className="flex-1 text-left truncate"
                    onClick={() => handleApply(v)}
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-accent rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePinMutation.mutate(v);
                    }}
                    aria-label={`Pin ${v.name}`}
                  >
                    <Pin className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    className="p-1 hover:bg-accent rounded text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(v);
                    }}
                    aria-label={`Delete ${v.name}`}
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="flex-1" />
        {rightSlot && <div className="flex items-center gap-2 shrink-0">{rightSlot}</div>}
      </div>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Name</label>
            <Input
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="e.g. NE region — Bookings only"
              maxLength={80}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConfirmSave();
              }}
              data-testid={`input-saved-view-name-${entity}`}
            />
            <p className="text-xs text-muted-foreground">
              Captures your current sort, filters, and search. Saved views are private to you.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmSave}
              disabled={!newName.trim() || createMutation.isPending}
              data-testid={`button-confirm-save-view-${entity}`}
            >
              {createMutation.isPending ? "Saving…" : "Save and pin"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
